// Hook para editar plan_ejercicios field-by-field via RPC
// actualizar_plan_ejercicio (migration 0015).
//
// Sprint 3 B4a PR1. Comportamiento:
//   - Debounce 500ms POR (plan_ejercicio_id, field). Cambios rápidos al
//     mismo campo coalecen en un solo RPC con el último valor.
//   - UI optimista: el caller actualiza su state local inmediato. Si la
//     RPC falla, el caller lee el error desde `errors` y revierte.
//   - Manejo de codes de la RPC mapeado a mensajes UX en español.
//   - Pending state expuesto para que la UI muestre spinner per-field.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../client";

export type EditableField =
  | "reps_objetivo"
  | "series_objetivo"
  | "descanso_seg"
  | "frecuencia_semanal"
  | "notas_kine";

export type EditableValue = number | string | null;

const DEBOUNCE_MS = 500;

interface RpcError {
  code: string;
  message: string;
}

/** Clave compuesta para debounce/pending/error tracking. */
function fieldKey(planEjId: string, field: EditableField): string {
  return `${planEjId}.${field}`;
}

/** Mapea code de la RPC a mensaje UX claro en español. */
function messageForCode(code: string, rawMessage?: string): string {
  switch (code) {
    case "NO_AUTH":
      return "Tu sesión expiró. Iniciá sesión de nuevo.";
    case "NOT_FOUND":
      return "No encontramos el ejercicio del plan. Refrescá la página.";
    case "TRATAMIENTO_CERRADO":
      return "Este tratamiento está cerrado — no se puede editar.";
    case "INVALID_FIELD":
      // Bug del cliente — log para diagnóstico, mensaje genérico al usuario.
      return "Error inesperado al guardar. Refrescá la página.";
    case "INVALID_VALUE":
      return "El valor ingresado no es válido para este campo.";
    case "UNKNOWN":
      return rawMessage ?? "No pudimos guardar. Probá de nuevo.";
    default:
      return rawMessage ?? "No pudimos guardar. Probá de nuevo.";
  }
}

export interface UseActualizarPlanEjercicioReturn {
  /** Disparar update (debounced). El value se envía al RPC 500ms después
   *  del último llamado para la misma (planEjId, field). */
  update: (
    planEjId: string,
    field: EditableField,
    value: EditableValue,
  ) => void;
  /** Set de keys "planEjId.field" actualmente en vuelo (RPC pending). */
  pending: Set<string>;
  /** Map<key, errorInfo> de errores recientes. La UI lee y muestra toast/inline. */
  errors: Map<string, RpcError>;
  /** Limpia el error de un (planEjId, field) específico — el caller llama
   *  esto post-dismiss del toast o al revertir el valor. */
  dismissError: (planEjId: string, field: EditableField) => void;
}

export function useActualizarPlanEjercicio(): UseActualizarPlanEjercicioReturn {
  // Timeouts del debounce per-key. Persiste entre renders.
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  // Último valor pendiente per-key (para que el timeout dispare con el más reciente).
  const pendingValuesRef = useRef<Map<string, EditableValue>>(new Map());

  const [pending, setPending] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Map<string, RpcError>>(new Map());

  // Cleanup en unmount — cancelar todos los timeouts pendientes.
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      for (const t of timeouts.values()) clearTimeout(t);
      timeouts.clear();
    };
  }, []);

  const fireRpc = useCallback(
    async (
      planEjId: string,
      field: EditableField,
      value: EditableValue,
    ) => {
      const key = fieldKey(planEjId, field);
      setPending((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      // Limpiar error previo (UI optimista — si falla, el catch re-setea).
      setErrors((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });

      try {
        const { data, error: rpcError } = await supabase.rpc(
          "actualizar_plan_ejercicio",
          {
            p_plan_ejercicio_id: planEjId,
            p_field: field,
            // jsonb param: el cliente Supabase serializa null/number/string OK.
            // El RPC valida jsonb_typeof y rechaza valores no soportados.
            p_value: value,
          },
        );

        if (rpcError) {
          // Error de transporte / postgrest. Tratamos como UNKNOWN.
          setErrors((prev) => {
            const next = new Map(prev);
            next.set(key, { code: "UNKNOWN", message: rpcError.message });
            return next;
          });
          return;
        }

        if (data && typeof data === "object" && data.ok === false) {
          const code = (data.code as string | undefined) ?? "UNKNOWN";
          const rawMessage = data.message as string | undefined;
          setErrors((prev) => {
            const next = new Map(prev);
            next.set(key, { code, message: messageForCode(code, rawMessage) });
            return next;
          });
        }
        // ok=true → nada que hacer, UI ya tiene el valor optimista.
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[useActualizarPlanEjercicio] throw", e);
        setErrors((prev) => {
          const next = new Map(prev);
          next.set(key, { code: "UNKNOWN", message: msg });
          return next;
        });
      } finally {
        setPending((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [],
  );

  const update = useCallback(
    (planEjId: string, field: EditableField, value: EditableValue) => {
      const key = fieldKey(planEjId, field);
      pendingValuesRef.current.set(key, value);

      // Cancel timeout anterior si existe.
      const existing = timeoutsRef.current.get(key);
      if (existing) clearTimeout(existing);

      // Nuevo timeout que dispara con el VALOR MÁS RECIENTE de pendingValuesRef.
      const t = setTimeout(() => {
        timeoutsRef.current.delete(key);
        const latestValue = pendingValuesRef.current.get(key);
        pendingValuesRef.current.delete(key);
        void fireRpc(planEjId, field, latestValue ?? null);
      }, DEBOUNCE_MS);
      timeoutsRef.current.set(key, t);
    },
    [fireRpc],
  );

  const dismissError = useCallback(
    (planEjId: string, field: EditableField) => {
      const key = fieldKey(planEjId, field);
      setErrors((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    },
    [],
  );

  return { update, pending, errors, dismissError };
}
